import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Typography,
  InputAdornment,
  Fade,
  Grow,
  Skeleton,
  Grid,
  Card,
  Stack,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import chatApi from './ChatApi';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Snackbar, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { chatPhotoUrl } from '../../utils/constants/links';
const LIMIT = 50;

const ChatCreation = ({
  selectChat,
  getChatList,
  method = null,
  chatRoomId,
  userIds = [],
  afterAddmemberToGroupThenBack,
}) => {
  // Method = Add to group , start chat
  const [searchQuery, setSearchQuery] = useState('');
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [grpMemberIds, setGrpMemberIds] = useState(userIds);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  const loginedUserId = React.useMemo(() => {
    try {
      const cred = localStorage.getItem('cred');
      return cred ? JSON.parse(cred)?.profile?.userId : null;
    } catch {
      return null;
    }
  }, []);

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
  };
  const listRef = useRef(null);

  useEffect(() => {
    fetchEmployees(1, true);
  }, [searchQuery]);

  const fetchEmployees = async (pageNumber, reset = false) => {
    if (loading || !hasMore) return;

    setLoading(true);

    const res = await chatApi.getAllEmployeesList({
      page: pageNumber,
      limit: LIMIT,
      search: searchQuery,
    });

    const newData = res?.data ?? [];

    setEmployees((prev) => (reset ? newData : [...prev, ...newData]));

    setHasMore(newData.length === LIMIT);
    setPage(pageNumber);
    setLoading(false);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loading) {
      fetchEmployees(page + 1);
    }
  };

  const startNewChat = async (otherUserId) => {
    try {
      setLoading(true);
      const res = await chatApi.createOrGetChatRoom(otherUserId);
      const CreatedchatRoomId = res?.data?.chat_room_id;
      if (!CreatedchatRoomId) return;
      const updatedChats = await getChatList();
      const exists = updatedChats.find((c) => c.chat_room_id === CreatedchatRoomId);
      if (exists) {
        selectChat(CreatedchatRoomId);
      }
    } catch (error) {
      console.error('❌ Error starting new chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const AddMember = async (memberIds) => {
    console.log('add member chatroom id', chatRoomId);

    try {
      setLoading(true);
      const res = await chatApi.addMemberToGroup(chatRoomId, [memberIds]);
      if (res) {
        showAlert('Member added to group!', 'success');
        setGrpMemberIds((prev) => [...prev, memberIds]);
      }
    } catch (error) {
      console.error('❌ Error adding member to group:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderSkeletons = () =>
    Array.from({ length: 6 }).map((_, i) => (
      <ListItem key={i} sx={{ padding: 2 }}>
        <ListItemAvatar>
          <Skeleton variant="circular" width={50} height={50} />
        </ListItemAvatar>
        <ListItemText sx={{ marginLeft: 2 }} primary={<Skeleton width="60%" />} secondary={<Skeleton width="40%" />} />
      </ListItem>
    ));

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          maxWidth: 600,
          width: '100%',
          height: '100vh',
          borderRadius: 0,
          bgcolor: '#f9ecff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          mb={1}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid #d4d4d4',
            //only need while this page render from grrp edit page mounting other wise 2 picture comes while home page creation mounting
            backgroundImage: method ? 'url("/static/illustrations/chatTop.png")' : 'none',

            // backgroundSize: 'cover',
            // backgroundPosition: 'center',
          }}
        >
          {afterAddmemberToGroupThenBack && (
            <Box
              sx={{
                width: 30,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <IconButton
                onClick={() => afterAddmemberToGroupThenBack()}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                }}
              >
                <ArrowBackIcon sx={{ color: '#fff', fontSize: 20 }} />
              </IconButton>
            </Box>
          )}

          <TextField
            fullWidth
            placeholder="Search by name ..."
            value={searchQuery}
            onChange={(e) => {
              setPage(1);
              setHasMore(true);
              setSearchQuery(e.target.value);
            }}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8f9ff', // 👈 background
                borderRadius: 2, // 👈 rounded corners

                '& fieldset': {
                  borderColor: '#e0e0e0',
                },

                '&:hover fieldset': {
                  borderColor: '#667eea',
                },

                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#667eea' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List
          ref={listRef}
          onScroll={handleScroll}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            paddingTop: 0,
            '&::-webkit-scrollbar': { width: '0.4em' },
          }}
        >
          {employees.map((data, index) => (
            <Stack
              key={`${data.userID}-${index}`}
              direction="row"
              alignItems="center"
              spacing={1.5}
              onClick={() => {
                if (method === 'add_to_group') {
                  return;
                } else {
                  if (data?.userID == loginedUserId) {
                    showAlert(
                      method === 'add_to_group' ? "You can't add yourself to group!" : "You can't chat with yourself!",
                      'error',
                    );
                    return;
                  }
                  startNewChat(data?.userID);
                }
              }}
              sx={{
                pb: 1,
                pr: 1.5,
                pl: 1.5,
                cursor: 'pointer',
              }}
            >
              {/* Avatar */}
              <Avatar
                src={`${chatPhotoUrl}/${data?.photo}.jpg`}
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: '0.9rem',
                  bgcolor: 'primary.light',
                  '& img': {
                    objectFit: 'cover', // 👈 fills avatar correctly
                  },
                }}
              >
                {data?.name?.[0]?.toUpperCase()}
              </Avatar>

              {/* Chat-style Box */}
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  flex: 1,
                  borderRadius: 2,
                  borderTopLeftRadius: 0,
                  bgcolor: 'grey.100',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    bgcolor: '#f8f9ff',
                    borderLeft: '4px solid #667eea',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {data?.name}
                </Typography>

                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {data?.email}
                </Typography>
              </Paper>
              {/* {method === 'add_to_group' && <AddIcon position="end" onClick={() => AddMember(data?.userID)} />} */}
              {method === 'add_to_group' &&
                (grpMemberIds?.includes(data?.userID) ? (
                  <CheckIcon color="success" />
                ) : loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <AddIcon disabled={true} onClick={() => AddMember(data?.userID)} sx={{ cursor: 'pointer' }} />
                ))}
            </Stack>
          ))}

          {loading && renderSkeletons()}

          {!loading && employees.length === 0 && (
            <Fade in={true}>
              <Box sx={{ p: 6, textAlign: 'center', color: '#999' }}>
                <Typography variant="h6">No employees found</Typography>
              </Box>
            </Fade>
          )}
        </List>
      </Paper>
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatCreation;
